import axios from 'axios';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

const api = axios.create({ baseURL: BASE, timeout: 10000 });

api.interceptors.request.use(config => {
  const token = localStorage.getItem('bg_access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Refresh token logic — prevents multiple simultaneous refresh calls
let _refreshing = false;
let _refreshQueue = [];

function clearAuth() {
  localStorage.removeItem('bg_access_token');
  localStorage.removeItem('bg_refresh_token');
}

function doLogout() {
  clearAuth();
  window.location.href = '/login';
}

async function tryRefresh() {
  const rt = localStorage.getItem('bg_refresh_token');
  if (!rt) throw new Error('no_refresh_token');
  const res = await axios.post(`${BASE}/auth/refresh`, { refreshToken: rt });
  const { accessToken, refreshToken } = res.data;
  localStorage.setItem('bg_access_token', accessToken);
  if (refreshToken) localStorage.setItem('bg_refresh_token', refreshToken);
  return accessToken;
}

api.interceptors.response.use(
  r => r.data,
  async err => {
    const original = err.config;
    const is401 = err.response?.status === 401;
    const isAuthEndpoint = original?.url?.includes('/auth/login') || original?.url?.includes('/auth/refresh');

    if (is401 && !isAuthEndpoint && !original._retry) {
      if (_refreshing) {
        // Queue this request until refresh completes
        return new Promise((resolve, reject) => {
          _refreshQueue.push({ resolve, reject, config: original });
        });
      }
      original._retry = true;
      _refreshing = true;
      try {
        const newToken = await tryRefresh();
        _refreshQueue.forEach(({ resolve, config }) => {
          config.headers.Authorization = `Bearer ${newToken}`;
          resolve(api(config));
        });
        _refreshQueue = [];
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch {
        _refreshQueue.forEach(({ reject }) => reject(err));
        _refreshQueue = [];
        doLogout();
      } finally {
        _refreshing = false;
      }
    }

    return Promise.reject(err.response?.data || err);
  }
);

export default api;

export const authApi = {
  login:        (phone, password) => api.post('/auth/login', { phone, password }),
  me:           ()               => api.get('/auth/me'),
  logout:       ()               => api.post('/auth/logout'),
  users:        ()               => api.get('/auth/users'),
  staff:        ()               => api.get('/auth/staff'),
  clients:      ()               => api.get('/auth/clients'),
  search:       (q)              => api.get(`/auth/search?q=${encodeURIComponent(q)}`),
  stats:        ()               => api.get('/auth/stats'),
  blockUser:    (id, block)      => api.patch(`/auth/users/${id}/block`, { block }),
  updateRole:   (id, data)       => api.patch(`/auth/users/${id}/role`, data),
  createStaff:  (data)           => api.post('/auth/create-staff', data),
  createClient: (data)           => api.post('/auth/create-client', data),
  deleteUser:   (id)             => api.delete(`/auth/users/${id}`),
  resetPassword:(id, newPassword)=> api.patch(`/auth/users/${id}/reset-password`, { newPassword }),
  unlockUser:   (id)             => api.patch(`/auth/users/${id}/unlock`),
};

export const settingsApi = {
  getModules:   ()       => api.get('/settings/modules'),
  getEnabled:   ()       => api.get('/settings/modules/enabled'),
  updateAll:    (updates)=> api.patch('/settings/modules', { updates }),
  updateOne:    (key, isEnabled) => api.patch('/settings/modules/' + key, { key, isEnabled }),
};

export const barrierApi = {
  list:         ()         => api.get('/barriers/all'),
  create:       (data)     => api.post('/barriers', data),
  update:       (id, data) => api.patch(`/barriers/${id}`, data),
  getOne:       (id)       => api.get(`/barriers/${id}`),
  open:         (id)       => api.post(`/barriers/${id}/open`),
  adminOpen:    (id)       => api.post(`/barriers/${id}/admin-open`),
  deleteBarrier:(id)       => api.delete(`/barriers/${id}`),
  logs:         (id)       => api.get(`/barriers/${id}/logs`),
  getUsers:     (id)       => api.get(`/barriers/${id}/users`),
  addUser:      (id, phone)=> api.post(`/barriers/${id}/users`, { phone }),
  removeUser:   (id, uid)  => api.delete(`/barriers/${id}/users/${uid}`),
  sendConfig:   (id, cfg)  => api.post(`/barriers/${id}/send-config`, cfg),
  // Firmware / OTA
  firmwareInfo: ()         => api.get('/barriers/firmware/info'),
  uploadFirmware: (file, version) => {
    const fd = new FormData();
    fd.append('file', file);
    if (version) fd.append('version', version);
    return api.post('/barriers/firmware/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  triggerOta:      (id)      => api.post(`/barriers/${id}/ota`),
  loadLocalFirmware: (version) => api.post('/barriers/firmware/load-local', { version }),
  debug:           ()        => api.get('/barriers/debug'),
  allLogs:         ()        => api.get('/barriers/logs/recent'),
  createGuestQr:   (id, data) => api.post(`/barriers/${id}/guest-qr`, data),
  listGuestQrs:    (id)       => api.get(`/barriers/${id}/guest-qrs`),
};

export const API_HOST = (process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1').replace('/api/v1', '');

export const restaurantApi = {
  list:               ()                   => api.get('/restaurants'),
  create:             (data)               => api.post('/restaurants', data),
  update:             (id, data)           => api.patch(`/restaurants/${id}`, data),
  getMenu:            (id)                 => api.get(`/restaurants/${id}/menu`),
  getTables:          (id)                 => api.get(`/restaurants/${id}/tables`),
  addCategory:        (id, name)           => api.post(`/restaurants/${id}/categories`, { name }),
  deleteCategory:     (catId)              => api.delete(`/restaurants/categories/${catId}`),
  addItem:            (catId, data)        => api.post(`/restaurants/categories/${catId}/items`, data),
  updateItem:         (itemId, data)       => api.patch(`/restaurants/items/${itemId}`, data),
  toggleItem:         (itemId)             => api.patch(`/restaurants/items/${itemId}/toggle`),
  deleteItem:         (itemId)             => api.delete(`/restaurants/items/${itemId}`),
  addTable:           (id, tableNumber)    => api.post(`/restaurants/${id}/tables`, { tableNumber }),
  kitchenOrders:      (id)                 => api.get(`/restaurants/${id}/kitchen`),
  updateOrderStatus:  (orderId, status)    => api.patch(`/restaurants/orders/${orderId}/status`, { status }),
  // Reviews
  getReviews:         (id)                 => api.get(`/restaurants/${id}/reviews`),
  updateReview:       (reviewId, data)     => api.patch(`/restaurants/reviews/${reviewId}`, data),
  deleteReview:       (reviewId)           => api.delete(`/restaurants/reviews/${reviewId}`),
  // Image uploads
  uploadImage: (restaurantId, type, file) => {
    const fd = new FormData();
    fd.append('image', file);
    return api.post(`/restaurants/${restaurantId}/upload?type=${type}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  uploadItemImage: (itemId, file) => {
    const fd = new FormData();
    fd.append('image', file);
    return api.post(`/restaurants/items/${itemId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  addGalleryImage: (restaurantId, fd) =>
    api.post(`/restaurants/${restaurantId}/gallery`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 30000 }),
  removeGalleryImage: (restaurantId, imageUrl) =>
    api.delete(`/restaurants/${restaurantId}/gallery`, { data: { imageUrl } }),
};

export const portalApi = {
  // Admin creates/manages portal staff via main admin JWT
  getStaff:     (restaurantId)        => api.get(`/restaurants/${restaurantId}/admin-staff`),
  createStaff:  (restaurantId, data)  => api.post(`/restaurants/${restaurantId}/admin-staff`, data),
  updateStaff:  (restaurantId, staffId, data) => api.patch(`/restaurants/${restaurantId}/admin-staff/${staffId}`, data),
  deleteStaff:  (restaurantId, staffId)       => api.delete(`/restaurants/${restaurantId}/admin-staff/${staffId}`),
  // Reports (admin token)
  getDashboard: (restaurantId)               => api.get(`/restaurants/${restaurantId}/dashboard`),
  getSalesReport:(restaurantId, from, to)    => api.get(`/restaurants/${restaurantId}/reports/sales`, { params: { from, to } }),
  getFullReport: (restaurantId, from, to)    => api.get(`/restaurants/${restaurantId}/reports/full`,  { params: { from, to } }),
  getTopItems:   (restaurantId, from, to)    => api.get(`/restaurants/${restaurantId}/reports/items`, { params: { from, to } }),
  getOrders:     (restaurantId, params)      => api.get(`/restaurants/${restaurantId}/orders`, { params }),
  getCosts:      (restaurantId, params)      => api.get(`/restaurants/${restaurantId}/costs`,  { params }),
};

export const rolesApi = {
  list:   ()         => api.get('/roles'),
  getOne: (id)       => api.get(`/roles/${id}`),
  create: (data)     => api.post('/roles', data),
  update: (id, data) => api.patch(`/roles/${id}`, data),
  remove: (id)       => api.delete(`/roles/${id}`),
};

export const walletApi = {
  balance: () => api.get('/wallet/balance'),
  transactions: () => api.get('/wallet/transactions'),
  topUp: (amount) => api.post('/wallet/topup', { amount }),
  adminAll: () => api.get('/wallet/admin/all'),
  adminTopUp: (userId, amount, description) => api.post(`/wallet/admin/topup/${userId}`, { amount, description }),
  adminTransactions: (userId) => api.get(`/wallet/admin/transactions/${userId}`),
};

export const announcementsApi = {
  list:   ()          => api.get('/announcements'),
  create: (data)      => api.post('/announcements', data),
  update: (id, data)  => api.patch(`/announcements/${id}`, data),
  remove: (id)        => api.delete(`/announcements/${id}`),
};

export const deliveryApi = {
  list:         (status) => api.get('/delivery', { params: status ? { status } : {} }),
  create:       (data)   => api.post('/delivery', data),
  updateStatus: (id, status) => api.patch(`/delivery/${id}/status`, { status }),
  remove:       (id)     => api.delete(`/delivery/${id}`),
};

export const reportsApi = {
  getBarrierStats: (from, to) => api.get('/barriers/stats', { params: { from, to } }),
  getUserStats:    (from, to) => api.get('/auth/stats',    { params: { from, to } }),
};

export const healthApi = {
  getSelf:       ()                   => api.get('/health'),
  getServices:   (forceRefresh = false) => api.get('/health/services', { params: forceRefresh ? { refresh: 'true' } : {} }),
  getService:    (key)               => api.get(`/health/services/${key}`),
};
